import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { formatDateTime, SituacaoGravacaoType } from '../../../../../../_metronic/helpers'
import { InfoAlert } from '../../../../../components'
import { type Gravacao } from '../core/_models'
import { listarGravacoes } from '../core/_requests'

type GravacoesAtendimentoProps = {
  idAtendimento: number
}

const formataDuracao = (segundos?: number): string => {
  if (segundos == null) {
    return '-'
  }
  const minutos = Math.floor(segundos / 60)
  const resto = segundos % 60
  if (minutos === 0) {
    return `${resto}s`
  }
  return `${minutos}min ${resto.toString().padStart(2, '0')}s`
}

const TrechosGravacao: FC<{ gravacao: Gravacao }> = ({ gravacao }) => {
  const [emReproducao, setEmReproducao] = useState<number | null>(null)
  const refs = useRef<Record<number, HTMLVideoElement | null>>({})

  const ordenados = useMemo(
    () => [...gravacao.arquivos].sort((a, b) => a.ordem - b.ordem),
    [gravacao.arquivos]
  )

  const aoTerminar = useCallback(
    (indice: number) => {
      const proximo = ordenados[indice + 1]
      if (!proximo?.arquivo?.url) {
        setEmReproducao(null)
        return
      }
      const elemento = refs.current[proximo.id]
      if (elemento) {
        setEmReproducao(proximo.id)
        void elemento.play()
      }
    },
    [ordenados]
  )

  return (
    <div className='d-flex flex-column gap-3'>
      {ordenados.map((item, indice) => {
        const idLegenda = `gravacao-trecho-legenda-${item.id}`
        return (
          <div key={item.id}>
            {ordenados.length > 1 && (
              <p id={idLegenda} className='text-muted fs-8 mb-1'>
                Trecho {item.ordem} de {ordenados.length}
                {emReproducao === item.id && ' · reproduzindo'}
              </p>
            )}
            {item.arquivo?.url ? (
              <video
                ref={(elemento) => {
                  refs.current[item.id] = elemento
                }}
                data-testid={`player-arquivo-${item.id}`}
                src={item.arquivo.url}
                controls
                preload='none'
                className='w-100 rounded'
                {...(ordenados.length > 1
                  ? { 'aria-labelledby': idLegenda }
                  : { 'aria-label': `Gravação de ${formatDateTime(gravacao.dtInicio)}` })}
                onPlay={() => setEmReproducao(item.id)}
                onEnded={() => aoTerminar(indice)}
              />
            ) : (
              <InfoAlert variant='warning'>Trecho {item.ordem} indisponível no momento.</InfoAlert>
            )}
          </div>
        )
      })}
    </div>
  )
}

export const GravacoesAtendimento: FC<GravacoesAtendimentoProps> = ({ idAtendimento }) => {
  const [gravacoes, setGravacoes] = useState<Gravacao[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState(false)

  useEffect(() => {
    let cancelado = false
    setCarregando(true)
    listarGravacoes(idAtendimento)
      .then((dados) => {
        if (!cancelado) {
          setGravacoes(dados)
          setErro(false)
        }
      })
      .catch(() => {
        if (!cancelado) {
          setErro(true)
        }
      })
      .finally(() => {
        if (!cancelado) {
          setCarregando(false)
        }
      })
    return () => {
      cancelado = true
    }
  }, [idAtendimento])

  const temPendente = useMemo(
    () =>
      gravacoes.some(
        (g) =>
          g.situacao === SituacaoGravacaoType.Encerrando ||
          g.situacao === SituacaoGravacaoType.Gravando ||
          g.situacao === SituacaoGravacaoType.Solicitada
      ),
    [gravacoes]
  )

  useEffect(() => {
    if (!temPendente) {
      return
    }
    let cancelado = false
    const timer = setInterval(() => {
      listarGravacoes(idAtendimento)
        .then((dados) => {
          if (!cancelado) {
            setGravacoes(dados)
          }
        })
        .catch(() => {
          //
        })
    }, 3000)

    return () => {
      cancelado = true
      clearInterval(timer)
    }
  }, [idAtendimento, temPendente])

  if (carregando) {
    return (
      <div className='text-muted' role='status'>
        Carregando gravações…
      </div>
    )
  }

  if (erro) {
    return (
      <InfoAlert variant='danger'>
        Não foi possível carregar as gravações deste atendimento.
      </InfoAlert>
    )
  }

  if (gravacoes.length === 0) {
    return (
      <div className='text-muted' role='status'>
        Nenhuma gravação para este atendimento.
      </div>
    )
  }

  return (
    <div className='d-flex flex-column gap-5'>
      {gravacoes.map((gravacao) => (
        <div key={gravacao.id} className='card'>
          <div className='card-header align-items-center'>
            <h3 className='card-title fs-5'>
              {formatDateTime(gravacao.dtInicio)} · {formataDuracao(gravacao.duracaoSegundos)}
            </h3>
          </div>
          <div className='card-body'>
            {gravacao.situacao === SituacaoGravacaoType.Disponivel &&
            gravacao.arquivos.length > 0 ? (
              <TrechosGravacao gravacao={gravacao} />
            ) : gravacao.situacao === SituacaoGravacaoType.Disponivel ? (
              <InfoAlert variant='warning'>
                A gravação foi concluída, mas nenhum arquivo ficou disponível para reprodução.
              </InfoAlert>
            ) : gravacao.situacao === SituacaoGravacaoType.Erro && gravacao.arquivos.length > 0 ? (
              <div className='d-flex flex-column gap-3'>
                <InfoAlert variant='warning'>
                  Esta gravação terminou de forma anormal e pode estar incompleta.
                </InfoAlert>
                <TrechosGravacao gravacao={gravacao} />
              </div>
            ) : gravacao.situacao === SituacaoGravacaoType.Erro ? (
              <InfoAlert variant='warning'>Esta gravação falhou e não gerou arquivo.</InfoAlert>
            ) : (
              <div className='text-muted d-flex align-items-center' role='status'>
                <span className='spinner-border spinner-border-sm me-2' aria-hidden='true' />
                <span>Processando o arquivo da gravação…</span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
